/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import React, { Component, ReactNode } from 'react';
import {
  Box,
  Container,
  Alert,
  Typography,
  Button,
  Paper,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Home as HomeIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';

// Error boundary state interface
interface StudioContextBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

// Error boundary props interface
interface StudioContextBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

/**
 * Error boundary component for studio context failures.
 * Provides graceful degradation and recovery options.
 */
export class StudioContextBoundary extends Component<
  StudioContextBoundaryProps,
  StudioContextBoundaryState
> {
  constructor(props: StudioContextBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): StudioContextBoundaryState {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { onError } = this.props;

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });

    // Call optional error handler
    if (onError) {
      onError(error, errorInfo);
    }

    // Log error for debugging
    console.error('Studio Context Error:', error);
    console.error('Error Info:', errorInfo);
  }

  handleRetry = () => {
    // Reset error state to retry
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  static handleGoHome = () => {
    // Navigate to home page
    window.location.href = '/';
  };

  static handleRefresh = () => {
    // Refresh the page
    window.location.reload();
  };

  render() {
    const { hasError } = this.state;
    const { fallback, children } = this.props;

    if (hasError) {
      // Custom fallback UI if provided
      if (fallback) {
        return fallback;
      }

      const { error, errorInfo } = this.state;

      // Default error UI
      return (
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
              }}
            >
              {/* Error Icon */}
              <ErrorIcon
                sx={{
                  fontSize: 64,
                  color: 'error.main',
                }}
              />

              {/* Error Title */}
              <Typography variant="h4" component="h1" gutterBottom>
                Studio Context Error
              </Typography>

              {/* Error Description */}
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 600, mb: 2 }}
              >
                There was an error loading the studio context. This might be due
                to a network issue, server problem, or invalid studio access.
              </Typography>

              {/* Error Details (Development Mode) */}
              {process.env.NODE_ENV === 'development' && error && (
                <Alert
                  severity="error"
                  sx={{ width: '100%', textAlign: 'left' }}
                >
                  <Typography variant="h6" gutterBottom>
                    Error Details:
                  </Typography>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: '0.875rem',
                    }}
                  >
                    {error.message}
                  </Typography>
                  {errorInfo && (
                    <>
                      <Typography variant="h6" sx={{ mt: 2 }} gutterBottom>
                        Component Stack:
                      </Typography>
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontSize: '0.75rem',
                        }}
                      >
                        {errorInfo.componentStack}
                      </Typography>
                    </>
                  )}
                </Alert>
              )}

              {/* Action Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleRetry}
                  size="large"
                >
                  Try Again
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={StudioContextBoundary.handleRefresh}
                  size="large"
                >
                  Refresh Page
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<HomeIcon />}
                  onClick={StudioContextBoundary.handleGoHome}
                  size="large"
                >
                  Go Home
                </Button>
              </Box>

              {/* Help Text */}
              <Typography variant="body2" color="text.secondary">
                If this problem persists, please contact support or try logging
                out and back in.
              </Typography>
            </Box>
          </Paper>
        </Container>
      );
    }

    // Render children normally if no error
    return children;
  }
}

/**
 * Functional wrapper for the Studio Context Boundary.
 * Provides a simpler interface for common use cases.
 */
export const StudioContextErrorBoundary: React.FC<{
  children: ReactNode;
  fallback?: ReactNode;
}> = ({ children, fallback }) => (
  <StudioContextBoundary
    fallback={fallback}
    onError={(error, _errorInfo) => {
      // Log to external error reporting service if available
      if ((window as any).gtag) {
        (window as any).gtag('event', 'exception', {
          description: `Studio Context Error: ${error.message}`,
          fatal: false,
        });
      }
    }}
  >
    {children}
  </StudioContextBoundary>
);

export default StudioContextBoundary;
